import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    Icon,
    Input,
} from "@elit-hair/elit-ui";
import { motion } from "framer-motion";
import { type KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbChevronLeft, TbChevronRight, TbUser } from "react-icons/tb";

import { slideRight } from "@/utils/animations";

const ITEMS_PER_PAGE = 25;

export type Group = {
    id: string;
    name: string;
    color?: string;
    items: Item[];
};

export type Item = {
    id: string;
    name?: string;
    email?: string;
    // Any additional data can be stored here
    [key: string]: unknown;
};

export type DoubleSelectGroups = {
    data: Group[];
};

export type DoubleSelectProps = {
    selectedItemId?: string | null;
    // Control the dialog from outside, e.g. to open it straight from another action
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    hideTrigger?: boolean;
    // Extra controls rendered at the top right of the dialog header
    headerActions?: React.ReactNode;
    onItemChange?: (itemId: string | null) => void;
    currentUserId?: string | null;
    groups?: DoubleSelectGroups;
    bottomGroup?: Group;
    isLoadingGroups?: boolean;
    isLoadingBottomGroup?: boolean;
    getGroupName?: (group: Group) => string;
    getGroupColor?: (group: Group) => string;
    getItemName?: (item: Item) => string;
    getItemEmail?: (item: Item) => string;
    getItemDisplayText?: (item: Item) => string;
    getItemIcon?: (item: Item) => React.ReactNode;
    sortItems?: (a: Item, b: Item, getItemName?: (item: Item) => string) => number;
    labels?: {
        selectGroupAndItem?: string;
        selectGroup?: string;
        selectItem?: string;
        selectItemFromGroup?: string;
        loading?: string;
        items?: string;
        noItemsInGroup?: string;
        page?: string;
        of?: string;
        confirm?: string;
        resetSelection?: string;
    };
    itemsPerPage?: number;
};

const DoubleSelectModal = ({
    isOpen,
    groups,
    bottomGroup,
    isLoadingGroups,
    selectedGroup,
    selectedItem,
    paginatedItems,
    currentItemsPage,
    onGroupSelect,
    onItemSelect,
    onConfirmItem,
    onItemsPageChange,
    onClose,
    onClear,
    onConfirm,
    memberSearch,
    onMemberSearchChange,
    isUserSearch,
    getGroupName,
    getGroupColor,
    getItemName,
    getItemEmail,
    getItemDisplayText,
    getItemIcon,
    headerActions,
    labels,
}: {
    isOpen: boolean;
    groups: DoubleSelectGroups | undefined;
    bottomGroup?: Group;
    isLoadingGroups: boolean | undefined;
    selectedGroup: string | null;
    selectedItem: string | null;
    paginatedItems: { items: Item[]; totalPages: number };
    currentItemsPage: number;
    onGroupSelect: (groupId: string) => void;
    onItemSelect: (itemId: string, groupId?: string) => void;
    onConfirmItem: (itemId: string, groupId?: string) => void;
    onItemsPageChange: (page: number) => void;
    onClose: () => void;
    onClear: () => void;
    onConfirm: () => void;
    memberSearch: string;
    onMemberSearchChange: (value: string) => void;
    isUserSearch: boolean;
    getGroupName?: (group: Group) => string;
    getGroupColor?: (group: Group) => string;
    getItemName?: (item: Item) => string;
    getItemEmail?: (item: Item) => string;
    getItemDisplayText?: (item: Item) => string;
    getItemIcon?: (item: Item) => React.ReactNode;
    headerActions?: React.ReactNode;
    labels?: DoubleSelectProps["labels"];
}) => {
    const { t } = useTranslation();
    const searchInputRef = useRef<HTMLInputElement>(null);
    const activeItemRef = useRef<HTMLDivElement>(null);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const listItems = paginatedItems.items;
    const activeIndex = listItems.length === 0 ? 0 : Math.min(highlightedIndex, listItems.length - 1);
    // Search results don't depend on the selected group, so picking a result (which selects its team) keeps the highlight.
    const listKey = isUserSearch ? `search:${memberSearch}` : `group:${selectedGroup}`;

    useEffect(() => {
        setHighlightedIndex(0);
    }, [listKey, currentItemsPage]);

    useEffect(() => {
        activeItemRef.current?.scrollIntoView({ block: "nearest" });
    }, [activeIndex, listKey, currentItemsPage]);

    const selectListItem = (item: Item) => {
        onItemSelect(item.id, typeof item.groupId === "string" ? item.groupId : undefined);
    };

    const confirmActiveItem = () => {
        const item = listItems[activeIndex];
        if (!item) {
            return;
        }

        onConfirmItem(item.id, typeof item.groupId === "string" ? item.groupId : undefined);
    };

    const handlePanelKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (listItems.length === 0) {
            return;
        }

        if (event.key === "ArrowDown") {
            event.preventDefault();
            setHighlightedIndex((current) => {
                const index = Math.min(current, listItems.length - 1);
                return Math.min(index + 1, listItems.length - 1);
            });
            return;
        }

        if (event.key === "ArrowUp") {
            event.preventDefault();
            setHighlightedIndex((current) => {
                const index = Math.min(current, listItems.length - 1);
                return Math.max(index - 1, 0);
            });
            return;
        }

        if (event.key === "Enter" && !(event.target instanceof HTMLButtonElement)) {
            event.preventDefault();
            confirmActiveItem();
        }
    };

    const renderGroup = (group: Group) => {
        const groupName = getGroupName ? getGroupName(group) : group.name;
        const groupColor = getGroupColor ? getGroupColor(group) : group.color;

        return (
            <div
                key={group.id}
                onClick={() => onGroupSelect(group.id)}
                className={`mr-1 flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1 transition-colors ${
                    selectedGroup === group.id ? "border-primary/20 bg-primary/10" : "border-transparent hover:bg-muted"
                }`}
            >
                <div
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{
                        backgroundColor: groupColor || "#ccc",
                    }}
                />
                <span className="min-w-0 flex-1 truncate text-xs font-medium">{groupName}</span>
                <span
                    className="shrink-0 text-xs tabular-nums text-muted-foreground opacity-70"
                    title={`${group.items.length} ${t(labels?.items || "items")}`}
                >
                    {group.items.length}
                </span>
            </div>
        );
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent
                className="flex h-[min(85vh,820px)] w-[min(95vw,1200px)] max-w-none flex-col p-2 sm:max-w-none"
                onKeyDown={handlePanelKeyDown}
                onOpenAutoFocus={(event) => {
                    event.preventDefault();
                    searchInputRef.current?.focus();
                }}
            >
                <DialogDescription className="sr-only">
                    {t(labels?.selectGroupAndItem || "select_team_and_member")}
                </DialogDescription>
                <DialogHeader className="border-b px-2 py-2">
                    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 pr-6">
                        <DialogTitle className="mb-0 justify-self-start text-md">
                            {t(labels?.selectGroupAndItem || "select_team_and_member")}
                        </DialogTitle>
                        <Input
                            ref={searchInputRef}
                            value={memberSearch}
                            onChange={(event) => onMemberSearchChange(event.target.value)}
                            placeholder={t("search_users")}
                            className="h-8 w-56 sm:w-72"
                            aria-label={t("search_users")}
                        />
                        {headerActions ? (
                            <div className="flex items-center gap-2 justify-self-end">{headerActions}</div>
                        ) : null}
                    </div>
                </DialogHeader>

                {/* Content Area */}
                <div className="flex flex-col lg:flex-row gap-2 sm:gap-4 flex-1 min-h-0 px-2 py-2 overflow-hidden">
                    {/* Group Selection */}
                    <div className="flex max-h-[35%] w-full min-h-0 flex-col lg:h-full lg:max-h-none lg:w-72 lg:shrink-0">
                        <h3 className="text-xs font-medium mb-1.5 sm:mb-2">
                            {t(labels?.selectGroup || "select_team")}
                        </h3>
                        {isLoadingGroups ? (
                            <div className="text-sm text-muted-foreground">{t(labels?.loading || "loading")}</div>
                        ) : (
                            <>
                                <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto">
                                    {groups?.data?.map((group) => renderGroup(group))}
                                </div>
                                {bottomGroup ? (
                                    <div className="mt-1 shrink-0 border-t pt-1">{renderGroup(bottomGroup)}</div>
                                ) : null}
                            </>
                        )}
                    </div>

                    {/* Item Selection */}
                    {(selectedGroup || isUserSearch) && (
                        <div className="flex min-h-0 w-full flex-1 flex-col lg:border-l lg:pl-4">
                            <h3 className="text-xs font-medium mb-1.5 sm:mb-2 shrink-0">
                                {t(labels?.selectItem || "select_member")}
                            </h3>
                            {paginatedItems.items.length === 0 ? (
                                <div className="text-xs text-muted-foreground">
                                    {t(
                                        isUserSearch ? "no_users_found" : labels?.noItemsInGroup || "no_members_in_team"
                                    )}
                                </div>
                            ) : (
                                <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                                    {/* Items List */}
                                    <motion.div {...slideRight} transition={{ duration: 0.2 }} className="space-y-0.5">
                                        {paginatedItems.items.map((item: Item, index) => {
                                            const itemName = getItemName ? getItemName(item) : item.name || item.id;
                                            const itemEmail = getItemEmail ? getItemEmail(item) : item.email;
                                            const itemDisplayText = getItemDisplayText
                                                ? getItemDisplayText(item)
                                                : null;
                                            const itemIcon = getItemIcon ? (
                                                getItemIcon(item)
                                            ) : (
                                                <Icon
                                                    icon={TbUser}
                                                    size={16}
                                                    className="text-muted-foreground shrink-0"
                                                />
                                            );
                                            const isSelected = index === activeIndex;
                                            const teamName =
                                                typeof item.groupName === "string" ? item.groupName : undefined;

                                            return (
                                                <div
                                                    key={`${typeof item.groupId === "string" ? item.groupId : "group"}-${item.id}`}
                                                    ref={index === activeIndex ? activeItemRef : undefined}
                                                    onClick={() => {
                                                        setHighlightedIndex(index);
                                                        selectListItem(item);
                                                    }}
                                                    className={`flex items-center justify-between rounded-md border px-2 py-1 cursor-pointer transition-colors ${
                                                        isSelected
                                                            ? "bg-primary/10 border-primary/20"
                                                            : "border-transparent hover:bg-muted"
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1">
                                                        {itemIcon}
                                                        <div className="flex min-w-0 flex-1 items-baseline gap-2">
                                                            <span className="shrink-0 truncate text-xs font-medium sm:max-w-[45%]">
                                                                {itemName}
                                                            </span>
                                                            {isUserSearch && teamName ? (
                                                                <span className="shrink-0 truncate text-xs text-muted-foreground opacity-70">
                                                                    {teamName}
                                                                </span>
                                                            ) : null}
                                                            {itemEmail && (
                                                                <span className="hidden min-w-0 truncate text-xs text-muted-foreground opacity-70 sm:inline">
                                                                    {itemEmail}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {itemDisplayText && (
                                                        <div className="text-xs text-muted-foreground shrink-0 ml-1 opacity-70">
                                                            {itemDisplayText}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </motion.div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex flex-col border-t">
                    <div className="flex flex-col sm:flex-row gap-2 justify-between items-center pt-2">
                        <div className="flex h-4 items-center justify-center min-w-0">
                            {paginatedItems.totalPages > 1 && (
                                <>
                                    <div className="text-xs text-muted-foreground mr-2">
                                        {t(labels?.page || "page")} {currentItemsPage} {t(labels?.of || "of")}{" "}
                                        {paginatedItems.totalPages}
                                    </div>
                                    <div className="flex gap-1">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => onItemsPageChange(currentItemsPage - 1)}
                                            disabled={currentItemsPage === 1}
                                            className="h-6 w-6 p-0"
                                        >
                                            <Icon icon={TbChevronLeft} size={12} />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => onItemsPageChange(currentItemsPage + 1)}
                                            disabled={currentItemsPage === paginatedItems.totalPages}
                                            className="h-6 w-6 p-0"
                                        >
                                            <Icon icon={TbChevronRight} size={12} />
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2 shrink-0">
                            <Button variant="outline" onClick={onClear} className="h-8 px-3 text-xs">
                                {t(labels?.resetSelection || "reset_selection")}
                            </Button>
                            <Button
                                variant="default"
                                onClick={onConfirm}
                                disabled={!selectedGroup || !selectedItem}
                                className="h-8 px-3 text-xs"
                            >
                                {t(labels?.confirm || "confirm")}
                            </Button>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export const DoubleSelect = ({
    selectedItemId,
    onItemChange,
    currentUserId,
    groups,
    bottomGroup,
    isLoadingGroups,
    isLoadingBottomGroup,
    getGroupName,
    getGroupColor,
    getItemName,
    getItemEmail,
    getItemDisplayText,
    getItemIcon,
    sortItems,
    labels,
    itemsPerPage = ITEMS_PER_PAGE,
    open,
    onOpenChange,
    hideTrigger = false,
    headerActions,
}: DoubleSelectProps) => {
    const { t } = useTranslation();
    const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
    const [selectedItem, setSelectedItem] = useState<string | null>(selectedItemId || null);
    const [isDoubleSelectModalOpen, setIsDoubleSelectModalOpen] = useState(false);
    const isModalOpen = open ?? isDoubleSelectModalOpen;
    const setModalOpen = useCallback(
        (nextOpen: boolean) => {
            setIsDoubleSelectModalOpen(nextOpen);
            onOpenChange?.(nextOpen);
        },
        [onOpenChange]
    );
    const [currentItemsPage, setCurrentItemsPage] = useState(1);
    const [memberSearch, setMemberSearch] = useState("");
    const isUserSearch = memberSearch.trim().length > 0;
    const selectableGroups = useMemo(() => {
        const data = groups?.data ?? [];
        return bottomGroup ? [...data, bottomGroup] : data;
    }, [groups?.data, bottomGroup]);

    // Sync with external selectedItemId
    useEffect(() => {
        setSelectedItem(selectedItemId || null);
    }, [selectedItemId]);

    // Find group for selected item
    useEffect(() => {
        if (!selectedItemId) {
            setSelectedGroup(null);
            return;
        }

        if (!groups?.data || isLoadingGroups || isLoadingBottomGroup) {
            return;
        }

        if (selectedItemId === currentUserId) {
            setSelectedGroup(null);
            return;
        }

        const itemGroup = selectableGroups.find((group) => group.items.some((item) => item.id === selectedItemId));

        setSelectedGroup(itemGroup?.id || null);
    }, [selectedItemId, groups?.data, isLoadingGroups, isLoadingBottomGroup, currentUserId, selectableGroups]);

    const nothingSelected = !selectedGroup && !selectedItem;
    const bothSelected = selectedGroup && selectedItem;
    const isCurrentUserSelected = selectedItem === currentUserId;

    // Find selected group
    const selectedGroupObject = useMemo(
        () => selectableGroups.find((group) => group.id === selectedGroup),
        [selectableGroups, selectedGroup]
    );

    const paginatedItems = useMemo(() => {
        const query = memberSearch.trim().toLowerCase();
        const itemLabel = (item: Item) => (getItemName ? getItemName(item) : item.name || item.id);
        const itemEmail = (item: Item) => (getItemEmail ? getItemEmail(item) : item.email || "");

        const source = query
            ? selectableGroups.flatMap((group) => {
                  const groupName = getGroupName ? getGroupName(group) : group.name;
                  return group.items
                      .filter((item) => {
                          const name = itemLabel(item).toLowerCase();
                          const email = itemEmail(item).toLowerCase();
                          return name.includes(query) || email.includes(query);
                      })
                      .map((item) => ({ ...item, groupId: group.id, groupName }));
              })
            : (selectedGroupObject?.items ?? []);

        const sortedItems = [...source].sort((a, b) => {
            if (sortItems) {
                return sortItems(a, b, getItemName);
            }

            return itemLabel(a).localeCompare(itemLabel(b));
        });

        const totalPages = Math.ceil(sortedItems.length / itemsPerPage);
        const startIndex = (currentItemsPage - 1) * itemsPerPage;
        const items = sortedItems.slice(startIndex, startIndex + itemsPerPage);

        return { items, totalPages };
    }, [
        currentItemsPage,
        getGroupName,
        getItemEmail,
        getItemName,
        itemsPerPage,
        memberSearch,
        selectableGroups,
        selectedGroupObject,
        sortItems,
    ]);

    const handleGroupSelect = useCallback((groupId: string) => {
        setSelectedGroup(groupId);
        setSelectedItem(null);
        setCurrentItemsPage(1);
        setMemberSearch("");
    }, []);

    const handleMemberSearchChange = useCallback((value: string) => {
        setMemberSearch(value);
        setCurrentItemsPage(1);
    }, []);

    const handleItemSelect = useCallback((itemId: string, groupId?: string) => {
        if (groupId) {
            setSelectedGroup(groupId);
        }
        setSelectedItem(itemId);
    }, []);

    const handleClear = useCallback(() => {
        setSelectedGroup(null);
        setSelectedItem(currentUserId ?? null);
        setCurrentItemsPage(1);
        setMemberSearch("");
        setModalOpen(false);
        onItemChange?.(currentUserId || null);
    }, [onItemChange, currentUserId, setModalOpen]);

    const handleConfirm = useCallback(() => {
        setModalOpen(false);
        setMemberSearch("");
        onItemChange?.(selectedItem);
    }, [selectedItem, onItemChange, setModalOpen]);

    const handleConfirmItem = useCallback(
        (itemId: string, groupId?: string) => {
            if (groupId) {
                setSelectedGroup(groupId);
            }
            setSelectedItem(itemId);
            setModalOpen(false);
            setMemberSearch("");
            setCurrentItemsPage(1);
            onItemChange?.(itemId);
        },
        [onItemChange, setModalOpen]
    );

    const toggleDoubleSelectModal = useCallback(() => {
        setModalOpen(!isModalOpen);
    }, [isModalOpen, setModalOpen]);

    const closeModal = useCallback(() => {
        setModalOpen(false);
        setMemberSearch("");
        setCurrentItemsPage(1);
    }, [setModalOpen]);

    const handleItemsPageChange = useCallback((page: number) => {
        setCurrentItemsPage(page);
    }, []);

    const groupColor = selectedGroupObject?.color || "#ccc";

    // Clean up invalid selections
    useEffect(() => {
        if (
            selectedItemId &&
            groups?.data &&
            !isLoadingGroups &&
            !isLoadingBottomGroup &&
            !selectableGroups.some((group) => group.items.some((item) => item.id === selectedItemId))
        ) {
            setSelectedItem(null);
        }
    }, [selectedItemId, groups?.data, isLoadingGroups, isLoadingBottomGroup, selectableGroups]);

    return (
        <div className="relative">
            {!hideTrigger && (
                <div
                    className="border border-border p-2 sm:p-2.5 rounded-md cursor-pointer hover:bg-muted transition-colors w-full flex items-center min-h-10"
                    role="button"
                    data-trigger="double-select"
                    onClick={toggleDoubleSelectModal}
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleDoubleSelectModal();
                        }
                    }}
                >
                    {nothingSelected && (
                        <div className="text-muted-foreground text-xs">
                            {isLoadingGroups
                                ? t(labels?.loading || "loading")
                                : t(labels?.selectGroupAndItem || "select_team_and_member")}
                        </div>
                    )}

                    {isCurrentUserSelected && !selectedGroup && (
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-primary" />
                            <span className="text-sm font-medium">
                                {t(labels?.resetSelection || "reset_selection")}
                            </span>
                        </div>
                    )}

                    {selectedGroup && !selectedItem && (
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: groupColor }} />
                            <span className="text-sm">
                                {t(labels?.selectItemFromGroup || "select_member_from_team")}{" "}
                                <span className="font-medium">{selectedGroupObject?.name}</span>
                            </span>
                        </div>
                    )}

                    {bothSelected && (
                        <div className="flex items-center gap-2">
                            <div
                                className={`w-3 h-3 rounded-full ${isCurrentUserSelected ? "bg-primary" : ""}`}
                                style={{
                                    backgroundColor: isCurrentUserSelected ? undefined : groupColor,
                                }}
                            />
                            <span className="text-sm">
                                {isCurrentUserSelected ? (
                                    <span className="font-medium">
                                        {t(labels?.resetSelection || "reset_selection")}
                                    </span>
                                ) : (
                                    <>
                                        <span className="font-medium">{selectedGroupObject?.name}</span>
                                        {" • "}
                                        <span className="font-medium">
                                            {selectedGroupObject?.items.find((item) => item.id === selectedItem)
                                                ?.name || selectedItem}
                                        </span>
                                    </>
                                )}
                            </span>
                        </div>
                    )}
                </div>
            )}

            <DoubleSelectModal
                isOpen={isModalOpen}
                groups={groups}
                bottomGroup={bottomGroup}
                isLoadingGroups={isLoadingGroups}
                selectedGroup={selectedGroup}
                selectedItem={selectedItem}
                paginatedItems={paginatedItems}
                currentItemsPage={currentItemsPage}
                onGroupSelect={handleGroupSelect}
                onItemSelect={handleItemSelect}
                onConfirmItem={handleConfirmItem}
                onItemsPageChange={handleItemsPageChange}
                onClose={closeModal}
                onClear={handleClear}
                onConfirm={handleConfirm}
                memberSearch={memberSearch}
                onMemberSearchChange={handleMemberSearchChange}
                isUserSearch={isUserSearch}
                getGroupName={getGroupName}
                getGroupColor={getGroupColor}
                getItemName={getItemName}
                getItemEmail={getItemEmail}
                getItemDisplayText={getItemDisplayText}
                getItemIcon={getItemIcon}
                headerActions={headerActions}
                labels={labels}
            />
        </div>
    );
};
